export const SNOW_VERT = `
varying vec2 vWorld;
varying vec2 vScreen;
uniform float uScale;
uniform float uCamX;
uniform float uCamY;
uniform float uViewW;
uniform float uViewH;
uniform vec2 uSnowCenter;
uniform vec2 uSnowSize;

void main() {
  vec2 screen = uSnowCenter + position.xy * uSnowSize;
  vScreen = screen;
  vWorld.x = (screen.x - uViewW * 0.5) / uScale + uCamX;
  vWorld.y = (uViewH - screen.y) / uScale + uCamY;
  gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4(position, 1.0);
}
`;

// The ground. Every noise field this used to evaluate per pixel -- nine fbm
// calls of five octaves, about 180 sines -- is now a texture tap of one of
// two small tiles baked by tools/art/terrain.cjs. Measured before the change,
// this one shader cost 18.6x a trivial one and was the whole GPU budget.
//
// Anything derived from the world position is declared highp whatever the
// material's default precision: vWorld reaches TRACK_LENGTH, 25600, where a
// 16-bit float already steps in whole units, and every UV here comes from it.
export const SNOW_FRAG = `
varying highp vec2 vWorld;
varying vec2 vScreen;
uniform sampler2D uSnow;
uniform sampler2D uTerrain;
uniform sampler2D uNoise;
uniform highp float uTime;
uniform float uSpeed;
uniform float uAir;
uniform float uCourseW;
uniform float uBank;
uniform float uViewH;
uniform float uReduced;
uniform float uSlope;
uniform float uGrainBump;
uniform vec3 uSunDir;
uniform vec3 uSnowCol;
uniform vec3 uPineCol;
uniform vec3 uIceCol;
uniform vec3 uSunCol;
uniform vec3 uSkyCol;

// Tile extents, matching tools/art/terrain.cjs.
const highp vec2 TERRAIN_WORLD = vec2(1024.0, 4096.0);
const highp float NOISE_PERIOD = 8.0;

void main() {
  highp vec2 w = vWorld;

  float tL = smoothstep(-28.0, 56.0, w.x);
  float tR = smoothstep(uCourseW + 28.0, uCourseW - 56.0, w.x);
  float lane = clamp(min(tL, tR), 0.0, 1.0);
  float bermL = smoothstep(-6.0, 22.0, w.x) * (1.0 - smoothstep(18.0, 54.0, w.x));
  float bermR = smoothstep(uCourseW + 6.0, uCourseW - 22.0, w.x) * (1.0 - smoothstep(uCourseW - 18.0, uCourseW - 54.0, w.x));
  float berm = max(bermL, bermR);

  highp vec2 uvSnow = (w / 210.0) * vec2(1.0, 0.9);
  vec4 snowTex = texture2D(uSnow, uvSnow);
  vec3 tex = snowTex.rgb;

  // The world-anchored fields in one tap: mounds in R, the ice field in G,
  // and in B and A the height differences the normal is built from.
  vec4 terrain = texture2D(uTerrain, w / TERRAIN_WORLD);
  float mounds = terrain.r;
  float iceMask = smoothstep(0.62, 0.84, terrain.g);

  // The scrolling fields. Each was fbm of an affine map of the world, and a
  // scroll of a tiling texture is a UV offset, so all three are taps of one
  // tile at three UV sets -- the anisotropy lives in the map, not the tile.
  highp float still = 1.0 - uReduced;
  highp vec2 pFine = w * 0.068 + vec2(0.0, uTime * 0.05 * still);
  highp vec2 pWind = vec2(w.x * 0.046, w.y * 0.12 - uTime * (0.55 + uSpeed * 0.002) * still);
  highp vec2 pRuts = vec2(w.x * 0.085, w.y * 0.014);
  float fine = texture2D(uNoise, pFine / NOISE_PERIOD).r;
  float wind = texture2D(uNoise, pWind / NOISE_PERIOD).g;
  float ruts = texture2D(uNoise, pRuts / NOISE_PERIOD).b;

  // The normal. B and A carry (h(x-e) - h(x+e)) and (h(y-e) - h(y+e)) of the
  // 0.02 field, scaled into 0..1 by uSlope, so this rebuilds the vector the
  // four finite-difference fbm calls used to produce. The snow tile's alpha
  // is a height field of the grain itself, and two more taps of it add the
  // granules' own relief on top.
  vec2 slope = (terrain.ba * 2.0 - 1.0) * uSlope;
  highp vec2 texel = vec2(1.0 / 512.0);
  float hA = snowTex.a;
  float hX = texture2D(uSnow, uvSnow + vec2(texel.x, 0.0)).a;
  float hY = texture2D(uSnow, uvSnow + vec2(0.0, texel.y)).a;
  slope += vec2(hA - hX, hA - hY) * uGrainBump;
  vec3 nrm = normalize(vec3(slope, 0.42));
  vec3 L = uSunDir;
  float ndl = clamp(dot(nrm, L), 0.0, 1.0);
  float wrap = ndl * 0.7 + 0.3;
  vec3 H = normalize(L + vec3(0.0, 0.0, 1.0));
  float spec = pow(clamp(dot(nrm, H), 0.0, 1.0), mix(28.0, 72.0, iceMask));

  // Sparkle from the tile's single-octave channel. At these scales the field
  // is under a screen pixel, which the procedural version aliased on; a
  // mipped tap fades to its own mean at distance instead of strobing.
  float spark = 0.0;
  if (uReduced < 0.5) {
    highp vec2 p1 = (w * 0.62 + vec2(uTime * 1.6, -uTime * 3.1)) / NOISE_PERIOD;
    highp vec2 p2 = (w * 1.05 - vec2(uTime * 2.4, 0.2)) / NOISE_PERIOD;
    spark = pow(max(0.0, texture2D(uNoise, p1).a - 0.78), 5.0);
    spark += pow(max(0.0, texture2D(uNoise, p2).a - 0.86), 6.0);
    spark *= 0.45 + uSpeed * 0.00055 + uAir * 0.25;
    spark *= 0.35 + ndl;
  }

  vec3 snow = uSnowCol;
  snow = mix(snow, tex, 0.22);
  snow += (mounds - 0.5) * 0.07;
  snow -= (1.0 - fine) * 0.035;
  snow -= (ruts - 0.42) * 0.08 * lane;
  snow = mix(snow, uIceCol, iceMask * 0.45 * lane);
  snow = mix(snow, uIceCol, wind * 0.12 * lane);

  vec3 lit = snow * (uSkyCol * 0.42 + uSunCol * wrap * 0.72);
  lit += uSunCol * spec * (0.12 + iceMask * 0.28);
  lit += vec3(0.95, 0.97, 1.0) * spark;

  vec3 bankSnow = mix(uPineCol, uSnowCol * vec3(0.78, 0.86, 0.9), 0.55 + mounds * 0.25);
  bankSnow *= 0.78 + 0.22 * fine;
  vec3 col = mix(bankSnow, lit, 0.22 + 0.78 * lane);
  col = mix(col, lit * 1.06, berm * 0.55);

  float woods = 1.0 - smoothstep(-uBank * 0.15, 8.0, w.x) * smoothstep(uCourseW + uBank * 0.15, uCourseW - 8.0, w.x);
  col = mix(col, uPineCol * (0.55 + mounds * 0.2), woods * 0.35);

  // The deep forest past the banks. On a phone this band is a sliver; a
  // height-fit desktop viewport shows a third of the frame out here, and a
  // flat pine fill read as void. Canopy crowns come from two taps of the
  // noise tile the shader already owns, snow-loaded on top, a breath of sun
  // on the brightest crowns -- and it deepens with distance from the lane, so
  // the sprite treeline stands against woods that fall away behind it.
  // Crown-sized frequencies: a tree top spans 25-40 world units, so the taps
  // repeat on that order -- the first cut sampled a period of 400 units and
  // read as one flat gradient, which is the void this exists to replace.
  float outside = max(-w.x, w.x - uCourseW);
  // uBank * 2.2 = 242: the canopy reaches full strength where the sprite
  // treeline ends -- BANK_INNER in src/game/course.ts. Retune them together.
  float deep = smoothstep(20.0, uBank * 2.2, outside);
  if (deep > 0.0) {
    float crowns = texture2D(uNoise, w * vec2(0.22, 0.18) / NOISE_PERIOD).g * 0.65
      + texture2D(uNoise, (w * 0.45 + 13.7) / NOISE_PERIOD).r * 0.35;
    vec3 forest = uPineCol * (0.55 + crowns * 0.9);
    forest = mix(forest, uSnowCol * 0.36, pow(crowns, 2.0) * 0.45);
    forest += uSunCol * pow(crowns, 3.0) * 0.05;
    col = mix(col, forest, deep * 0.85);
  }

  float ridge = smoothstep(uViewH * 0.62, uViewH * 0.98, vScreen.y);
  float hollow = smoothstep(uViewH * 0.52, 0.0, vScreen.y);
  col += uSunCol * ridge * 0.055;
  col = mix(col, vec3(0.10, 0.15, 0.14), hollow * 0.38);

  gl_FragColor = vec4(col, 1.0);
}
`;

export const SPRITE_VERT = `
varying vec2 vUv;
varying float vFog;
varying vec3 vInstColor;
varying vec2 vSunUv;
varying float vFacing;
uniform float uViewH;
uniform vec3 uSunDir;

void main() {
  vUv = uv;
  #ifdef USE_INSTANCING_COLOR
    vInstColor = instanceColor;
  #else
    vInstColor = vec3(1.0);
  #endif

  #ifdef USE_INSTANCING
    mat4 m = modelMatrix * instanceMatrix;
  #else
    mat4 m = modelMatrix;
  #endif
  vec4 wp = m * vec4(position, 1.0);
  vFog = clamp(1.0 - wp.y / max(uViewH, 1.0), 0.0, 1.4);

  // The sun on screen, y up. World y runs down the hill, so it flips. The
  // facing term is how far this corner sits toward the sun from the
  // sprite's own centre, interpolated into a gradient across the quad --
  // measured in screen space rather than texture space, so a mirrored tree
  // is still lit on the side the sun is on.
  vec2 sunScreen = normalize(vec2(uSunDir.x, -uSunDir.y));
  vFacing = dot(normalize(wp.xy - m[3].xy), sunScreen) * 0.5 + 0.5;
  // The same direction in texture space, for the rim tap: mirrored quads
  // carry a negative x scale, which m[0][0] exposes.
  float flip = m[0][0] < 0.0 ? -1.0 : 1.0;
  vSunUv = vec2(sunScreen.x * flip, sunScreen.y);

  gl_Position = projectionMatrix * viewMatrix * wp;
}
`;

export const SPRITE_FRAG = `
varying vec2 vUv;
varying float vFog;
varying vec3 vInstColor;
varying vec2 vSunUv;
varying float vFacing;
uniform sampler2D map;
uniform vec3 uFogCol;
uniform vec3 uSunCol;
uniform float uFogAmt;
uniform vec2 uTexel;

void main() {
  vec4 tex = texture2D(map, vUv);
  // Mip averaging pulls a thin silhouette's average alpha down, so a cutoff
  // tuned against the full-resolution image erodes pine needles and branch
  // tips as the tree recedes. 0.18 was that cutoff; this is low enough that
  // minified sprites keep their shape.
  if (tex.a < 0.08) discard;
  vec3 col = tex.rgb * vInstColor;

  // Lit on the sun's side, not from above. Same strength the old top-down
  // gradient had, so the sprites do not get brighter, only directional.
  float sunLit = pow(clamp(vFacing * 1.4 - 0.2, 0.0, 1.0), 1.4) * 0.22;

  // Rim light: the silhouette edge that faces the sun. Look a few texels
  // toward the sun; where there is nothing there, this pixel is that edge.
  // Reads as backlight through needles, and it is two taps.
  float beyond = texture2D(map, vUv + vSunUv * uTexel * 3.0).a;
  float rim = (1.0 - smoothstep(0.05, 0.35, beyond)) * 0.45;

  float near = 1.0 - clamp(vFog, 0.0, 1.0) * 0.7;
  col += uSunCol * (sunLit + rim) * near;
  col = mix(col, uFogCol, clamp(vFog * 0.5 * uFogAmt, 0.0, 0.68));
  gl_FragColor = vec4(col, tex.a);
}
`;

export const COMPOSITE_VERT = `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

export const COMPOSITE_FRAG = `
varying vec2 vUv;
uniform sampler2D tDiffuse;
uniform float uTime;
uniform float uSpeed;
uniform float uAir;
uniform float uStun;
uniform float uReduced;
uniform vec2 uRes;
uniform vec2 uPlayer;
uniform float uPhaseTitle;
// Where the disc sits, derived from the one sun direction in sun.ts.
uniform vec2 uSunPos;
// 1 when the world pass rendered into a half-float target, so values above
// 1 survived to here and the filmic curve has something to roll off. On the
// 8-bit tier the target already clipped, and the curve would only lift the
// mids, so it is bypassed and that tier looks as it did.
uniform float uHdr;
uniform float uExposure;
// The world pass renders into a sub-rect of a full-size target so its
// resolution can change without reallocating the drawing buffer. Screen-space
// effects below keep working in 0..1 output space; only the texture reads are
// remapped into the occupied part of the target.
uniform vec2 uUvScale;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

vec3 samp(vec2 uv) {
  return texture2D(tDiffuse, clamp(uv, 0.0, 1.0) * uUvScale).rgb;
}

vec3 sampleBright(vec2 uv) {
  vec3 c = samp(uv);
  float l = dot(c, vec3(0.299, 0.587, 0.114));
  return c * smoothstep(0.62, 0.9, l);
}

// Narkowicz's ACES fit: a filmic shoulder in six operations. Sun on snow
// reads as bright rather than as white paint because the highlights roll
// off instead of clipping.
vec3 filmic(vec3 x) {
  return clamp((x * (2.51 * x + 0.03)) / (x * (2.43 * x + 0.59) + 0.14), 0.0, 1.0);
}

void main() {
  vec2 uv = vUv;
  vec2 sunPos = uSunPos;
  vec2 fromPlayer = uv - uPlayer;
  float edge = smoothstep(0.18, 0.92, length(fromPlayer * vec2(1.2, 1.05)));
  float ca = (uSpeed * 0.0026 + uAir * 0.0016) * edge * (1.0 - uReduced);
  vec2 caOff = vec2(ca, ca * 0.2);

  vec3 col;
  col.r = samp(uv + caOff).r;
  col.g = samp(uv).g;
  col.b = samp(uv - caOff).b;

  vec3 bloom = sampleBright(uv) * 0.16;
  vec2 px = 1.0 / max(uRes, vec2(1.0));
  bloom += sampleBright(uv + vec2(3.2, 0.0) * px) * 0.08;
  bloom += sampleBright(uv - vec2(3.2, 0.0) * px) * 0.08;
  bloom += sampleBright(uv + vec2(0.0, 3.2) * px) * 0.08;
  bloom += sampleBright(uv - vec2(0.0, 3.2) * px) * 0.08;
  bloom += sampleBright(uv + vec2(5.5, 2.4) * px) * 0.05;
  bloom += sampleBright(uv + vec2(-5.5, 2.4) * px) * 0.05;
  col += bloom * (0.32 + uAir * 0.18);

  float sunD = length((uv - sunPos) * vec2(1.35, 1.85));
  float sunCore = exp(-sunD * 28.0);
  float sunHalo = exp(-sunD * 7.2);
  vec3 sunCol = vec3(1.0, 0.93, 0.78);
  col += sunCol * sunCore * (0.22 + uPhaseTitle * 0.1);
  col += vec3(0.82, 0.9, 1.0) * sunHalo * 0.07;

  float shaft = pow(max(0.0, 1.0 - abs(uv.x - sunPos.x) * 2.35), 2.8);
  shaft *= pow(max(0.0, uv.y - 0.34), 1.7);
  float flicker = uReduced > 0.5 ? 1.0 : 0.9 + 0.1 * sin(uTime * 0.41 + uv.y * 6.0);
  col += sunCol * shaft * flicker * (0.08 + uSpeed * 0.12 + uPhaseTitle * 0.05);

  if (uReduced < 0.5) {
    vec2 dir = sunPos - uv;
    vec2 p = uv;
    vec2 stepDir = dir / 14.0;
    float decay = 1.0;
    float illum = 0.0;
    for (int i = 0; i < 14; i++) {
      p += stepDir;
      vec3 s = samp(p);
      float lum = dot(s, vec3(0.299, 0.587, 0.114));
      illum += max(0.0, lum - 0.62) * decay;
      decay *= 0.86;
    }
    col += sunCol * illum * (0.032 + uSpeed * 0.04);
  }

  if (uSpeed > 0.1 && uReduced < 0.5) {
    float streaks = 0.0;
    for (int i = 0; i < 10; i++) {
      float fi = float(i);
      float sx = hash(vec2(fi * 17.17, floor(uTime * (26.0 + uSpeed * 70.0))));
      float sy = hash(vec2(fi * 9.1 + 2.4, floor(uTime * (16.0 + uSpeed * 38.0) + 3.0)));
      float dx = abs(uv.x - sx);
      float along = (1.0 - uv.y) - sy * 0.55;
      float line = smoothstep(0.0026, 0.0, dx) * step(0.0, along) * smoothstep(0.2 + uSpeed * 0.2, 0.0, along);
      streaks += line;
    }
    col += vec3(0.92, 0.96, 1.0) * streaks * (0.09 + uSpeed * 0.15);
  }

  col = mix(col, col * vec3(0.93, 0.98, 1.07), 0.32);
  col += vec3(0.015, 0.02, 0.035) * uAir;

  float mist = smoothstep(0.42, 0.0, uv.y) * (0.22 + uSpeed * 0.12);
  col = mix(col, vec3(0.09, 0.13, 0.12), mist * 0.7);

  float vig = smoothstep(1.18, 0.22, length((uv - vec2(0.5, 0.7)) * vec2(1.08, 1.18)));
  col *= mix(0.52, 1.0, vig);

  float frost = pow(edge, 2.4);
  float n = hash(uv * uRes * 0.28 + vec2(11.0, 3.0));
  col = mix(col, col * vec3(0.86, 0.94, 1.05) + 0.03, frost * 0.28 * (0.45 + n));

  col = mix(col, vec3(dot(col, vec3(0.33))) * vec3(0.52, 0.48, 0.42), uStun * 0.7);

  col = mix(clamp(col, 0.0, 1.0), filmic(col * uExposure), uHdr);

  // Two different things that used to be one. Film grain is a look, kept
  // deliberate and small; the old amplitude was seven times a dither's and
  // was masking banding with visible noise. The dither itself is one least
  // significant bit, added last, which is what actually removes banding
  // from a near-flat field like a snow lane.
  float g = hash(uv * uRes + vec2(uTime * 37.0, uTime * 19.0));
  col += (g - 0.5) * 0.012 * (1.0 - uReduced);
  float d = hash(uv * uRes + vec2(fract(uTime) * 31.0, 7.0));
  col += (d - 0.5) / 255.0;

  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
}
`;

export const SNOWFALL_VERT = `
attribute float aSeed;
uniform float uTime;
uniform float uSpeed;
uniform vec2 uRes;
uniform float uReduced;
// Point sizes are in target pixels, so they have to track the world pass's
// render scale or the flakes would swell as the resolution drops.
uniform float uPixelScale;
varying float vAlpha;
varying float vSize;

void main() {
  float px = fract(sin(aSeed * 19.1) * 43758.5453);
  float py = fract(sin(aSeed * 7.7) * 43758.5453);
  float pz = fract(sin(aSeed * 3.3) * 43758.5453);
  float drift = (pz - 0.5) * 36.0;
  float spd = 48.0 + uSpeed * 0.24 + pz * 110.0;
  float x = fract(px + uTime * (0.012 + pz * 0.028) + drift * 0.00018) * (uRes.x + 40.0) - 20.0;
  float y = fract(py + uTime * spd / max(uRes.y, 1.0)) * (uRes.y + 40.0) - 20.0;
  vec2 ndc = vec2((x / uRes.x) * 2.0 - 1.0, 1.0 - (y / uRes.y) * 2.0);
  gl_Position = vec4(ndc, 0.0, 1.0);
  vSize = mix(1.05, 3.8, pz);
  gl_PointSize = vSize * (uReduced > 0.5 ? 0.75 : 1.4) * clamp(uRes.y / 700.0, 0.75, 1.45) * uPixelScale;
  vAlpha = mix(0.14, 0.78, pz);
}
`;

export const SNOWFALL_FRAG = `
varying float vAlpha;
varying float vSize;
void main() {
  vec2 p = gl_PointCoord * 2.0 - 1.0;
  float d = dot(p, p);
  if (d > 1.0) discard;
  float a = (1.0 - smoothstep(0.12, 1.0, d)) * vAlpha;
  gl_FragColor = vec4(vec3(0.96, 0.98, 1.0), a);
}
`;

export const SPRAY_VERT = `
uniform float uScale;
uniform float uPixelScale;
attribute float aLife;
attribute float aSize;
varying float vLife;
void main() {
  vLife = aLife;
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * mv;
  gl_PointSize = max(1.2, aSize * uScale * (0.5 + aLife * 1.05) * uPixelScale);
}
`;

export const SPRAY_FRAG = `
varying float vLife;
uniform vec3 uColor;
void main() {
  vec2 p = gl_PointCoord * 2.0 - 1.0;
  float d = dot(p, p);
  if (d > 1.0) discard;
  float a = (1.0 - smoothstep(0.0, 1.0, d)) * vLife * 0.9;
  gl_FragColor = vec4(uColor, a);
}
`;
